/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { To } from "react-router"
import { createSearchParams, generatePath } from "react-router"

import { AppRoutes } from "@/routes/routes"
import type { UserList } from "./user-types"

/**
 * Builds the user detail location, including the organization the user is viewed in.
 *
 * The detail page uses `orgId` to determine how the user relates to the current
 * organization, so it is passed in the URL rather than looked up again.
 *
 * @param userId - ID of the user to open.
 * @param orgId - Organization the user is viewed in.
 * @returns A location with the user ID in the path and the organization ID in the query.
 */
export function userDetailLocation(userId: number, orgId: number): To {
    return {
        pathname: generatePath(AppRoutes.UserDetail, { id: `${userId}` }),
        search: `?${createSearchParams({ orgId: `${orgId}` })}`
    }
}

/**
 * Resolves where a row of the users list leads.
 *
 * An external user has no `organizationId`, so it is opened in the selected
 * organization instead.
 *
 * @param user - User from the list.
 * @param selectedOrgId - Organization selected in the console.
 * @returns The user detail location.
 */
export function userRowLocation(user: UserList, selectedOrgId: number): To {
    // A listed user is a saved row with an ID; the view keys its rows on it too.
    return userDetailLocation(user.id as number, user.organizationId ?? selectedOrgId)
}
